import React, { useEffect, useState } from "react";
import styles from "./CalendarGrid.module.css";
import BeltDrawer, { BeltOption } from "../BeltDrawer";
import { DirectPDFGenerator } from "../../services/directPdfGenerator";

interface Event {
  title: string;
  start: string; // ISO date
  end: string;   // ISO date
  location: string;
  populations: string[];
  description: string;
}

interface Season {
  startYear: number;
  endYear: number;
}

interface EventsData {
  season: Season;
  events: Event[];
}

const monthMapping: Record<number, string> = {
  0: "Janvier",
  1: "Février", 
  2: "Mars",
  3: "Avril",
  4: "Mai",
  5: "Juin",
  8: "Septembre",
  9: "Octobre",
  10: "Novembre",
  11: "Décembre"
};

const weekends = [
  "1er Week end du mois",
  "2e Week end du mois",
  "3e Week end du mois",
  "4e Week end du mois",
  "5e Week end du mois (quand il y en a un)"
];

const populationLabels: Record<string, string> = {
  CEB00: "Ceinture Enfant Bleue 0",
  CEB01: "Ceinture Enfant Bleue 1",
  CEB02: "Ceinture Enfant Bleue 2",
  CEB03: "Ceinture Enfant Bleue 3",
  CEO01: "Ceinture Enfant Orange 1",
  CEO02: "Ceinture Enfant Orange 2",
  CEO03: "Ceinture Enfant Orange 3",
  CEV01: "Ceinture Enfant Verte 1",
  CEV02: "Ceinture Enfant Verte 2",
  CEV03: "Ceinture Enfant Verte 3",
  CB00: "Ceinture Bleue 0",
  CB01: "Ceinture Bleue 1",
  CB02: "Ceinture Bleue 2",
  CB03: "Ceinture Bleue 3",
  CN: "Ceinture Noire",
  CJ01: "Ceinture Jaune 1",
  CJ02: "Ceinture Jaune 2",
  CJ03: "Ceinture Jaune 3",
  MS: "Maître Stagiaire",
  M: "Maître",
};

const beltImages: Record<string, string> = {
  CEB00: "/ceintures/CEB00.svg",
  CEB01: "/ceintures/CEB01.svg",
  CEB02: "/ceintures/CEB02.svg",
  CEB03: "/ceintures/CEB03.svg",
  CEO01: "/ceintures/CEO01.svg",
  CEO02: "/ceintures/CEO02.svg",
  CEO03: "/ceintures/CEO03.svg",
  CEV01: "/ceintures/CEV01.svg",
  CEV02: "/ceintures/CEV02.svg",
  CEV03: "/ceintures/CEV03.svg",
  CB00: "/ceintures/CB00.svg",
  CB01: "/ceintures/CB01.svg",
  CB02: "/ceintures/CB02.svg",
  CB03: "/ceintures/CB03.svg",
  CN: "/ceintures/CN.svg",
  CJ01: "/ceintures/CJ01.svg",
  CJ02: "/ceintures/CJ02.svg",
  CJ03: "/ceintures/CJ03.svg",
  MS: "/ceintures/MS.svg",
  M: "/ceintures/M.svg",
};

const allPopulations = Object.keys(populationLabels);

const beltGroups = [
  { key: "CE", label: "Ceintures enfant", values: ["CEB00", "CEB01", "CEB02", "CEB03", "CEO01", "CEO02", "CEO03", "CEV01", "CEV02", "CEV03"] },
  { key: "CB", label: "Ceintures bleues", values: ["CB00", "CB01", "CB02", "CB03"] },
  { key: "CJ", label: "Ceintures jaunes", values: ["CN", "CJ01", "CJ02", "CJ03"] },
  { key: "M", label: "Maitres", values: [ "MS", "M"] },
  { key: "cadres", label: "Cadres", values: ["CB03", "CN", "CJ01", "CJ02", "CJ03",  "MS", "M"] }
];
const beltOptions: BeltOption[] = allPopulations.map(code => ({ value: code, label: populationLabels[code] }));

// Helper pour obtenir les dates des week-ends d'un mois donné
function getWeekendDates(year: number, monthIndex: number, weekendNumber: number): number[] {
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  let weekends: number[][] = [];
  let currentWeekend: number[] = [];
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, monthIndex, d);
    if (date.getDay() === 6 || date.getDay() === 0) {
      currentWeekend.push(d);
      if (date.getDay() === 0 || d === daysInMonth) {
        weekends.push([...currentWeekend]);
        currentWeekend = [];
      }
    }
  }
  return weekends[weekendNumber - 1] || [];
}

// Helper pour obtenir le mois (string) et le week-end (number) à partir d'une date
function getMonthAndWeekend(dateStr: string): { month: string; weekend: number } {
  const d = new Date(dateStr);
  const monthIdx = d.getMonth();
  const month = monthMapping[monthIdx];
  
  // Trouver le week-end du mois pour ce jour
  const year = d.getFullYear();
  const weekends = [];
  let currentWeekend: number[] = [];
  const daysInMonth = new Date(year, monthIdx + 1, 0).getDate();
  
  for (let day = 1; day <= daysInMonth; day++) {
    const date = new Date(year, monthIdx, day);
    if (date.getDay() === 6 || date.getDay() === 0) {
      currentWeekend.push(day);
      if (date.getDay() === 0 || day === daysInMonth) {
        weekends.push([...currentWeekend]);
        currentWeekend = [];
      }
    }
  }
  
  // Trouver à quel week-end appartient le jour
  let weekendIdx = 0;
  for (let i = 0; i < weekends.length; i++) {
    if (weekends[i].includes(d.getDate())) {
      weekendIdx = i + 1;
      break;
    }
  }
  
  return { month, weekend: weekendIdx };
}

const seasonMonths = [
  { idx: 8, label: "Septembre" },
  { idx: 9, label: "Octobre" },
  { idx: 10, label: "Novembre" },
  { idx: 11, label: "Décembre" },
  { idx: 0, label: "Janvier" },
  { idx: 1, label: "Février" },
  { idx: 2, label: "Mars" },
  { idx: 3, label: "Avril" },
  { idx: 4, label: "Mai" },
  { idx: 5, label: "Juin" },
];

// Fonction pour obtenir le nom court du mois
const getShortMonthName = (fullName: string): string => {
  const shortNames: { [key: string]: string } = {
    "Septembre": "Sept",
    "Octobre": "Oct",
    "Novembre": "Nov",
    "Décembre": "Déc",
    "Janvier": "Jan",
    "Février": "Fév",
    "Mars": "Mars",
    "Avril": "Avr",
    "Mai": "Mai",
    "Juin": "Juin"
  };
  return shortNames[fullName] || fullName;
};

const CalendarGrid: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [season, setSeason] = useState<Season | null>(null);
  const [selectedPopulations, setSelectedPopulations] = useState<string[]>(allPopulations);

  useEffect(() => {
    fetch("/events.json")
      .then((res) => res.json())
      .then((data: EventsData) => {
        setEvents(data.events);
        setSeason(data.season);
      });
  }, []);

  if (!season) {
    return null;
  }

  const currentSeasonStartYear = season.startYear;
  const currentSeasonEndYear = season.endYear;

  // Helper pour obtenir les événements pour une cellule (mois, week-end)
  const getEvents = (monthIdx: number, weekend: number, year: number) => {
    const filteredEvents = events.filter((e) => {
      const startDate = new Date(e.start);
      const endDate = new Date(e.end);
      
      // Obtenir les informations de début et fin
      const { weekend: startWeekend } = getMonthAndWeekend(e.start);
      const { weekend: endWeekend } = getMonthAndWeekend(e.end);
      
      const startMonth = startDate.getMonth();
      const endMonth = endDate.getMonth();
      const startYear = startDate.getFullYear();
      const endYear = endDate.getFullYear();
      
      // Vérifier si cette cellule correspond exactement au début ou à la fin de l'événement
      const isStartCell = (monthIdx === startMonth && weekend === startWeekend && year === startYear);
      const isEndCell = (monthIdx === endMonth && weekend === endWeekend && year === endYear);
      
      // L'événement est visible uniquement dans ses cellules de début et de fin
      const isVisible = isStartCell || isEndCell;
      
      if (!isVisible) return false;
      
      // Si populations est vide, l'événement est ouvert à toutes les ceintures
      const isOpenToAll = e.populations.length === 0;
      const matchesPopulation = isOpenToAll || e.populations.some((pop) => selectedPopulations.includes(pop));
      
      const matches = isVisible && matchesPopulation;
      if (matches) {
        console.log(`Event "${e.title}" visible in mois ${monthIdx}, weekend ${weekend}, année ${year} (${e.start} - ${e.end}) - ${isStartCell ? 'START' : 'END'}`);
      }
      return matches;
    });
    return filteredEvents;
  };

  // Export ICS (téléchargement du fichier ICS public déjà généré)
  const handleExportICS = () => {
    const icsUrl = window.location.origin + '/events.ics';
    const link = document.createElement('a');
    link.href = icsUrl;
    link.download = 'uvv_calendar.ics';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      await DirectPDFGenerator.generateDirectPDF();
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
    }
  };

  // Export Google Agenda (URL directe pour synchronisation automatique)
  const handleExportGoogle = () => {
    try {
      // URL directe de l'ICS en ligne pour synchronisation automatique
      const icsUrl = `${window.location.origin}/events.ics`;
      
      // Ouvrir Google Calendar avec l'URL de l'ICS
      const googleCalendarUrl = `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(icsUrl)}`;
      
      // Ouvrir dans un nouvel onglet
      window.open(googleCalendarUrl, '_blank');
      
      // Afficher les instructions manuelles
      setTimeout(() => {
        alert(
          '1. Ouvrez Google Calendar\n' +
          '2. Cliquez sur le "+" à côté de "Autres calendriers"\n' +
          '3. Sélectionnez "S\'abonner au calendrier"\n' +
          '4. Collez cette URL : ' + icsUrl + '\n' +
          '5. Cliquez sur "Ajouter au calendrier"'
        );
      }, 100);
      
    } catch (error) {
      console.error('Erreur lors de l\'export Google Calendar:', error);
      alert('Erreur lors de l\'export. Veuillez réessayer.');
    }
  };

  return (
    <div className={styles.calendarGridContainer}>
      {/* Boutons d'export et filtre ceintures au-dessus du calendrier sur desktop */}
      <div className={styles.exportButtonsWrapper + ' ' + styles.exportDesktopOnly}>
        <div className={styles.leftSection}>
          <BeltDrawer
            value={selectedPopulations}
            onChange={setSelectedPopulations}
            options={beltOptions}
            groups={beltGroups}
          />
        </div>
        <div className={styles.rightSection}>
          <button onClick={handleExportPDF} className={styles.pdfButton}>
            Exporter en PDF
          </button>
          <button onClick={handleExportICS} className={styles.icsButton}>
            Exporter en ICS
          </button>
          <button onClick={handleExportGoogle} className={styles.icsButton}>
            Exporter vers Google Agenda
          </button>
        </div>
      </div>
      {/* Calendrier */}
      <div className={styles.tableWrapper}>
        <table
          id="calendar-grid-table"
          className={styles.calendarTable}
        >
          <thead>
            <tr>
              <th className={styles.monthHeader}></th>
              {seasonMonths.map(({label}) => (
                <th key={label} className={styles.monthHeader}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weekends.map((weekendLabel, wIdx) => (
              <tr key={weekendLabel}>
                <td className={styles.weekendHeader}>{weekendLabel}</td>
                {seasonMonths.map(({idx: mIdx, label}, colIdx) => {
                  // Calculer l'année du mois courant
                  const year = mIdx >= 8 ? currentSeasonStartYear : currentSeasonEndYear;
                  const weekendDays = getWeekendDates(year, mIdx, wIdx + 1);
                  const dayLabel = weekendDays.length > 0 ? `${weekendDays.join(" - ")} ${getShortMonthName(label)}` : "";
                  return (
                    <td key={label} className={styles.calendarCell}>
                      <div className={styles.cellDayLabel}>{dayLabel}</div>
                      {getEvents(mIdx, wIdx + 1, year).map((event) => (
                        <div key={event.title + event.start} className={styles.eventCard}>
                          <div className={styles.eventDate} data-event-date>
                            {(() => {
                              const start = new Date(event.start);
                              const end = new Date(event.end);
                              const sameDay = event.start === event.end;
                              const formatDate = (date: Date) => {
                                return date.toLocaleDateString('fr-FR', { 
                                  day: '2-digit', 
                                  month: '2-digit' 
                                });
                              };
                              return sameDay
                                ? formatDate(start)
                                : `${formatDate(start)} - ${formatDate(end)}`;
                            })()}
                          </div>
                          <div className={styles.eventTitle} data-event-title>{event.title}</div>
                          <div className={styles.eventPopulations}>
                            {event.populations.length === 0 ? (
                              <div className={styles.allBeltsBadge}>
                                Toutes ceintures
                              </div>
                            ) : (
                              event.populations.map((pop) => (
                                <img
                                  key={pop}
                                  src={beltImages[pop]}
                                  alt={pop}
                                  title={pop}
                                  className={styles.beltIcon}
                                />
                              ))
                            )}
                          </div>
                          <div className={styles.eventLocation} data-event-location>{event.location}</div>
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Boutons d'export en bas sur mobile uniquement */}
      <div className={styles.exportButtonsWrapper + ' ' + styles.exportMobileOnly}>
        <BeltDrawer
          value={selectedPopulations}
          onChange={setSelectedPopulations}
          options={beltOptions}
          groups={beltGroups}
        />
        <button onClick={handleExportPDF} className={styles.pdfButton}>
          Exporter en PDF
        </button>
        <button onClick={handleExportICS} className={styles.icsButton}>
          Exporter en ICS
        </button>
        <button onClick={handleExportGoogle} className={styles.icsButton}>
          Exporter vers Google Agenda
        </button>
      </div>
    </div>
  );
};

export default CalendarGrid; 